import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {
  openPdfCanonico,
  usePdfCanonico,
  usePuntajes,
  useTranscripcion,
  type BateriaPaquete,
  type EstructuraCuestionario,
  type EstructuraItem,
  type PuntajeEntidad,
  type Transcripcion,
  type TranscripcionItem,
} from '@/hooks/bateria/use-bateria'
import {useFormaEstructura} from '@/hooks/bateria/use-bateria'
import {NIVEL_TONE, prettifyTaxonomia} from '@/lib/bateria'
import {TituloDimension} from './titulo-dimension'
import {translateApiError} from '@/lib/errors'

/** Admin-only, Inci's clinical custody (Res. 2764/2022 art. 5), never visible to the tenant. Read-only:
 * never an answerable questionnaire (Res. 2764/2022 only allows paper or SIRPSI). */
export function ResultadoPaqueteDialog({
  businessId,
  paquete,
  open,
  onOpenChange,
}: {
  businessId: string
  paquete: BateriaPaquete
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const {t} = useTranslation()
  const {data: transcripcion} = useTranscripcion(businessId, open ? paquete.id : undefined)
  const {data: estructura} = useFormaEstructura(open ? paquete.forma : undefined, open)
  const {data: puntajes} = usePuntajes(businessId, open ? paquete.id : undefined, open)
  const {data: pdf} = usePdfCanonico(businessId, open ? paquete.id : undefined, open)
  const [pdfError, setPdfError] = useState<string | null>(null)

  async function openPdf() {
    setPdfError(null)
    try {
      await openPdfCanonico(businessId, paquete.id)
    } catch (err) {
      setPdfError(translateApiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="resultado-paquete-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.resultado.title', {name: paquete.employee_name})}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground" data-testid="resultado-custodia-note">
          {t('admin.bateria.resultado.custodiaNote')}
        </p>

        <div className="space-y-6">
          <section className="space-y-2" data-testid="resultado-pdf">
            <SectionLabel>{t('admin.bateria.resultado.pdfTitle')}</SectionLabel>
            {pdf ? (
              <>
                <CanonicoThumbnail
                  thumbnailUrl={pdf.thumbnail_url}
                  alt={t('admin.bateria.resultado.pdfAlt', {name: paquete.employee_name})}
                />
                <p className="text-xs text-muted-foreground">{t('admin.bateria.resultado.pdfPreviaNota')}</p>
                <Button size="sm" variant="outline" data-testid="resultado-pdf-descargar" onClick={openPdf}>
                  {t('admin.bateria.resultado.pdfOpen')}
                </Button>
                {pdfError && <p className="text-xs text-destructive">{pdfError}</p>}
              </>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="resultado-pdf-none">
                {t('admin.bateria.resultado.pdfNone')}
              </p>
            )}
          </section>

          <section className="space-y-2" data-testid="resultado-puntajes">
            <SectionLabel>{t('admin.bateria.resultado.puntajesTitle')}</SectionLabel>
            {!puntajes ? (
              <p className="text-sm text-muted-foreground">{t('admin.bateria.resultado.puntajesNone')}</p>
            ) : (
              <PuntajesView puntajes={puntajes} />
            )}
          </section>

          <section className="space-y-2" data-testid="resultado-respuestas">
            <SectionLabel>{t('admin.bateria.resultado.respuestasTitle')}</SectionLabel>
            <p className="text-xs text-muted-foreground">{t('admin.bateria.resultado.respuestasNote')}</p>
            {!estructura || !transcripcion ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              estructura.cuestionarios.map((c) => (
                <RespuestasCuestionario key={c.cuestionario} cuestionario={c} transcripcion={transcripcion} />
              ))
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function SectionLabel({children}: {children: React.ReactNode}) {
  return <p className="font-mono text-[11px] font-medium tracking-wider text-muted-foreground uppercase">{children}</p>
}

/** An `<img>` of the backend PNG: an embedded `<object data=pdf>` renders inconsistently across browsers. */
function CanonicoThumbnail({thumbnailUrl, alt}: {thumbnailUrl: string | null; alt: string}) {
  const {t} = useTranslation()
  const [failed, setFailed] = useState(false)
  if (!thumbnailUrl || failed) {
    return (
      <div
        className="flex h-72 w-full items-center justify-center rounded border border-dashed border-border text-sm text-muted-foreground"
        data-testid="resultado-pdf-visor">
        {t('admin.bateria.resultado.pdfSinVistaPrevia')}
      </div>
    )
  }
  return (
    <img
      src={thumbnailUrl}
      alt={alt}
      onError={() => setFailed(true)}
      className="max-h-72 w-full rounded border border-border bg-secondary object-contain"
      data-testid="resultado-pdf-visor"
    />
  )
}

function NivelTag({entidad}: {entidad: PuntajeEntidad}) {
  const {t} = useTranslation()
  if (!entidad.nivel) return <Tag tone="neutral">{t('admin.bateria.nivel.sinDato')}</Tag>
  return <Tag tone={NIVEL_TONE[entidad.nivel] ?? 'neutral'}>{t(`admin.bateria.nivel.${entidad.nivel}`)}</Tag>
}

function PuntajeRow({label, entidad}: {label: string; entidad: PuntajeEntidad}) {
  const {t} = useTranslation()
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="truncate text-sm text-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-2">
        {entidad.puntaje_transformado !== null && (
          <span className="font-mono text-[11px] text-muted-foreground">
            {t('admin.bateria.resultados.promedio', {value: entidad.puntaje_transformado.toFixed(1)})}
          </span>
        )}
        <NivelTag entidad={entidad} />
      </span>
    </div>
  )
}

function PuntajesView({puntajes}: {puntajes: NonNullable<ReturnType<typeof usePuntajes>['data']>}) {
  const {t} = useTranslation()
  const cuestionarios = Object.entries(puntajes.cuestionarios)
  const dominios = Object.entries(puntajes.dominios)
  const dimensiones = Object.entries(puntajes.dimensiones)
  return (
    <div className="space-y-3">
      <div className="divide-y divide-border">
        <PuntajeRow label={t('admin.bateria.resultados.totalGeneral')} entidad={puntajes.total_general} />
        {cuestionarios.map(([nombre, e]) => (
          <PuntajeRow key={nombre} label={t(`admin.bateria.cuestionario.${nombre}`, nombre)} entidad={e} />
        ))}
      </div>
      {dominios.length > 0 && (
        <div>
          <SectionLabel>{t('admin.bateria.resultados.dominiosTitle')}</SectionLabel>
          <div className="divide-y divide-border">
            {dominios.map(([nombre, e]) => (
              <PuntajeRow key={nombre} label={prettifyTaxonomia(nombre)} entidad={e} />
            ))}
          </div>
        </div>
      )}
      {dimensiones.length > 0 && (
        <div>
          <SectionLabel>{t('admin.bateria.resultados.dimensionesTitle')}</SectionLabel>
          <div className="divide-y divide-border">
            {dimensiones.map(([nombre, e]) => (
              <PuntajeRow key={nombre} label={prettifyTaxonomia(nombre)} entidad={e} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function itemLeido(transcripcion: Transcripcion, itemId: string): TranscripcionItem | undefined {
  return transcripcion.items.find((it) => it.item_id === itemId)
}

/** El enunciado viaja en la transcripción: es el del papel de esta persona (`plantilla_version`), no
 * el que se imprime hoy. */
function RespuestaFila({item, leido}: {item: EstructuraItem; leido: TranscripcionItem | undefined}) {
  const {t} = useTranslation()
  const value = leido?.value
  return (
    <div className="flex items-baseline gap-3 py-1.5" data-testid={`resultado-item-${item.item_id}`}>
      <span className="w-6 shrink-0 text-right font-mono text-[11px] text-muted-foreground">{item.numero}.</span>
      <span className="min-w-0 flex-1 text-sm text-foreground">{leido?.enunciado}</span>
      {/* Ancho fijo: en 123 filas las respuestas tienen que leerse como una columna. */}
      <span
        className={
          value ? 'w-32 shrink-0 text-right text-sm' : 'w-32 shrink-0 text-right text-sm text-muted-foreground italic'
        }>
        {value ? t(`admin.bateria.escala.${value}`, value) : t('admin.bateria.resultado.sinRespuesta')}
      </span>
    </div>
  )
}

function RespuestasCuestionario({
  cuestionario,
  transcripcion,
}: {
  cuestionario: EstructuraCuestionario
  transcripcion: Transcripcion
}) {
  const {t} = useTranslation()
  return (
    <section className="space-y-2" data-testid={`resultado-respuestas-${cuestionario.cuestionario}`}>
      <p className="font-heading text-base text-foreground">
        {t(`admin.bateria.cuestionario.${cuestionario.cuestionario}`, cuestionario.cuestionario)}
      </p>
      {cuestionario.grupos.map((grupo, gi) => (
        <div key={grupo.dimension ?? gi} className="surface p-3">
          {grupo.dimension && <TituloDimension dominio={grupo.dominio} dimension={grupo.dimension} />}
          <div className="divide-y divide-border">
            {grupo.items.map((item) => (
              <RespuestaFila key={item.item_id} item={item} leido={itemLeido(transcripcion, item.item_id)} />
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}
